import { useEffect, useRef, useState } from 'react'
import {
  ShieldCheck, KeyRound, MonitorSmartphone, LogOut, Palette, RefreshCw, Copy,
  UserCircle, Fingerprint, Plus, Trash2, Upload, Lock,
} from 'lucide-react'
import { startRegistration } from '@simplewebauthn/browser'
import { useMe, useMeReload } from './shell/AppShell.js'
import { initialsOf } from './shell/Sidebar.js'
import {
  apiGet, apiMutate, adminMutate, adminUpload, useToast, Modal,
} from './admin/primitives.js'
import { useSaveBar } from './shell/editing.js'
import { NotificationPrefsSection } from './NotificationPrefs.js'
import { MobileDevicesSection } from './MobileDevices.js'
import { CropperModal } from './admin/Cropper.js'
import { uploadPath, type CropFraction } from './admin/media-staging.js'
import { ConfirmDialog } from './auth/Dialog.js'
import { Link } from './router.js'

/**
 * /profile (LOT 5.5.1) — MON compte, désormais ÉDITABLE champ par champ.
 *
 * · Identité (prénom, nom, nom affiché, e-mail, téléphone, avatar) : UNE
 *   unité d'édition → Save Bar globale. Chaque champ est gardé par SA
 *   permission (profile.name.write / email.write / phone.write /
 *   avatar.write) — l'interface l'affiche, le BACKEND l'applique. Un champ
 *   non autorisé est en lecture seule, clairement dit.
 * · Changer d'e-mail est sensible : le serveur exige un step-up — la Save
 *   Bar le déclenche à la volée, rien n'est perdu.
 * · Sécurité & MFA : TOTP, CLÉS D'ACCÈS (passkeys) et méthode préférée.
 *   Ajouter/supprimer une passkey sont des actions ATOMIQUES (step-up +
 *   cérémonie WebAuthn) — pas des formulaires, donc pas de Save Bar.
 */

interface OwnSession {
  id: string; current: boolean; created_at: string; last_seen_at: string
  ip: string | null; user_agent: string | null
  remembered: boolean; absolute_expires_at: string
}
interface Passkey {
  id: string; label: string | null; created_at: string; last_used_at: string | null
  transports: string[] | null; backed_up: boolean | null; device_type: string | null
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function deviceSummary(ua: string | null): string {
  if (!ua) return 'Appareil inconnu'
  const browser = /Firefox\//.test(ua) ? 'Firefox' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Navigateur'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} · ${os}` : browser
}

export function ProfilePage() {
  const me = useMe()
  const reloadMe = useMeReload()
  const toast = useToast()
  const [sessions, setSessions] = useState<OwnSession[]>([])
  const [confirm, setConfirm] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)

  const chargerSessions = () => {
    apiGet<OwnSession[]>('/api/v1/auth/sessions').then(setSessions).catch(() => undefined)
  }
  useEffect(chargerSessions, [])

  useEffect(() => {
    const id = window.location.hash.slice(1)
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' })
  }, [])

  if (!me) {
    return (
      <main className="lc-page lc-stagger">
        <h1 className="lc-title">Mon profil</h1>
        <p className="lc-subtitle">Aucune session active.</p>
        <div className="adm-note" style={{ maxWidth: 620 }}>
          Cette page présente VOTRE compte : elle n’a de contenu qu’une fois
          connecté. L’authentification n’étant pas encore imposée, vous
          consultez actuellement le portail sans session —{' '}
          <a href="/login">se connecter</a>.
        </div>
      </main>
    )
  }

  const regenerate = async () => {
    const r = await apiMutate<{ codes?: string[] }>('POST', '/api/v1/auth/recovery/regenerate')
    if (r.status === 200 && Array.isArray(r.data.codes) && r.data.codes.length > 0) setCodes(r.data.codes)
    else toast('error', 'Régénération refusée — une ré-authentification forte récente peut être requise.')
  }

  return (
    <main className="lc-page lc-stagger">
      <header className="pf-head">
        <span className="lc-avatar pf-avatar">
          {me.avatar ? <img className="lc-avatar__img" src={me.avatar} alt="" /> : initialsOf(me.display_name)}
        </span>
        <div>
          <h1 className="lc-title">{me.display_name}</h1>
          <p className="lc-subtitle" style={{ margin: 0 }}>{me.email} · {me.role_label}</p>
        </div>
      </header>

      {/* ═══ Identité (Save Bar) ═══ */}
      <IdentitySection onSaved={reloadMe} />

      <div className="pf-grid" style={{ marginTop: 'var(--sp-4)' }}>
        {/* ═══ Sécurité & MFA ═══ */}
        <section className="lc-panel gd-panel" id="security" aria-labelledby="pf-sec">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="pf-sec"><ShieldCheck size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Sécurité & MFA</h2>
          </header>
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            <div className="ad-fact">
              <span className="ad-fact__label">Application d’authentification (TOTP)</span>
              <span className="ad-fact__value">
                {me.mfa_enabled
                  ? <span className="lc-pill lc-pill--ok"><ShieldCheck size={13} />Actif</span>
                  : <span className="lc-pill lc-pill--warning">À activer</span>}
                {!me.mfa_enabled ? <a className="lc-btn" href="/login/mfa-setup">Activer</a> : null}
              </span>
            </div>
            <div className="ad-fact">
              <span className="ad-fact__label">Codes de secours restants</span>
              <span className="ad-fact__value">
                <span className={`lc-pill ${me.recovery_codes_remaining <= 2 ? 'lc-pill--warning' : 'lc-pill--idle'}`}>
                  {me.recovery_codes_remaining} / 10
                </span>
                <button className="lc-btn" onClick={() => void regenerate()}>
                  <RefreshCw size={14} /> Régénérer
                </button>
              </span>
            </div>
            <div className="ad-fact">
              <span className="ad-fact__label">Mot de passe</span>
              <span className="ad-fact__value" style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                Modifiable ci-dessous — le changer déconnecte vos autres appareils.
              </span>
            </div>
          </div>
          <div className="lc-panel__body" style={{ padding: 'var(--sp-4) var(--sp-6) var(--sp-5)', borderTop: '1px solid var(--line-1)' }}>
            <PasskeySection me={{ passkeys: me.passkeys, mfa_enabled: me.mfa_enabled, preferred: me.preferred_mfa }} onChanged={reloadMe} />
          </div>
        </section>

        {/* ═══ Changer le mot de passe ═══ */}
        <section className="lc-panel gd-panel" aria-labelledby="pf-pwd">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="pf-pwd"><KeyRound size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Changer le mot de passe</h2>
          </header>
          <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
            <PasswordForm />
          </div>
        </section>
      </div>

      {/* ═══ Codes de secours fraîchement régénérés ═══ */}
      {codes ? (
        <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }} aria-label="Nouveaux codes de secours">
          <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
            <div className="adm-note" style={{ marginTop: 0 }}>
              <strong>Notez ces codes maintenant</strong> — ils ne seront plus jamais
              affichés, et les anciens codes sont révoqués.
            </div>
            <div className="pf-codes">
              {codes.map((c) => <span key={c} className="u-mono">{c}</span>)}
            </div>
            <div className="ad-actions">
              <button className="lc-btn" onClick={() => { void navigator.clipboard?.writeText(codes.join('\n')); toast('ok', 'Codes copiés') }}>
                <Copy size={14} /> Copier
              </button>
              <button className="lc-btn lc-btn--ghost" onClick={() => setCodes(null)}>J’ai noté mes codes</button>
            </div>
          </div>
        </section>
      ) : null}

      {/* ═══ Sessions ═══ */}
      <section className="lc-panel gd-panel" id="sessions" style={{ marginTop: 'var(--sp-4)' }} aria-labelledby="pf-ses">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="pf-ses"><MonitorSmartphone size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Mes sessions</h2>
          <span className="lc-panel__spacer" />
          {sessions.length > 1 ? (
            <button className="lc-btn" onClick={() => setConfirm(true)}>
              <LogOut size={14} /> Se déconnecter partout
            </button>
          ) : null}
        </header>
        <div className="lc-panel__body lc-panel__body--flush ad-facts">
          {sessions.map((s) => (
            <div key={s.id} className="ad-fact">
              <span>
                <div style={{ fontSize: 'var(--fs-14)', fontWeight: s.current ? 600 : 400 }}>
                  {deviceSummary(s.user_agent)}{s.current ? ' — session courante' : ''}
                </div>
                <div className="ad-mono" style={{ color: 'var(--text-3)' }}>
                  {s.ip ?? 'IP inconnue'} · active {fmt(s.last_seen_at)} · ouverte {fmt(s.created_at)}
                </div>
                <div style={{ fontSize: 'var(--fs-12)', color: 'var(--text-3)' }}>
                  {s.remembered
                    ? `Session persistante — jusqu'au ${new Date(s.absolute_expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`
                    : 'Session standard'}
                </div>
              </span>
              {s.remembered ? <span className="lc-pill lc-pill--idle">30 jours</span> : null}
              {s.current ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Ici</span> : null}
            </div>
          ))}
          {sessions.length === 0 ? (
            <div className="ad-fact"><span className="ad-fact__label">Aucune session listée.</span></div>
          ) : null}
        </div>
      </section>

      <NotificationPrefsSection />

      <MobileDevicesSection />

      {/* ═══ Préférences ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }} aria-label="Préférences">
        <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)', display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <Palette size={18} style={{ color: 'var(--text-2)', flex: 'none' }} />
          <p className="lc-card__desc" style={{ flex: 1 }}>
            Thème, accent, ambiance, animations, densité, sidebar… — vos
            préférences visuelles sont enregistrées sur votre compte et vous
            suivent d’un navigateur à l’autre.
          </p>
          <Link className="lc-btn" href="/settings/appearance">Ouvrir Apparence</Link>
        </div>
      </section>

      <ConfirmDialog
        open={confirm}
        danger={false}
        title="Se déconnecter de tous les appareils ?"
        confirmLabel="Tout déconnecter"
        onConfirm={async () => {
          setConfirm(false)
          await apiMutate('POST', '/api/v1/auth/sessions/revoke-all')
          window.location.assign('/login')
        }}
        onClose={() => setConfirm(false)}
      >
        Toutes vos sessions — y compris celle-ci — seront révoquées
        immédiatement. Vous devrez vous reconnecter.
      </ConfirmDialog>
    </main>
  )
}

/* ═══════════════ IDENTITÉ — l'unité d'édition de la page ═══════════════ */

function IdentitySection(props: { onSaved: () => void }) {
  const me = useMe()!
  const can = (p: string) => me.permissions.includes(p)
  const canName = can('profile.name.write')
  const canEmail = can('profile.email.write')
  const canPhone = can('profile.phone.write')
  const canAvatar = can('profile.avatar.write')

  const [first, setFirst] = useState(me.first_name ?? '')
  const [last, setLast] = useState(me.last_name ?? '')
  const [display, setDisplay] = useState(me.display_name)
  const [email, setEmail] = useState(me.email)
  const [phone, setPhone] = useState(me.phone ?? '')
  const [avatarStaged, setAvatarStaged] = useState<{ file: File; url: string; crop?: CropFraction } | 'delete' | null>(null)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [cropOpen, setCropOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    setFirst(me.first_name ?? ''); setLast(me.last_name ?? '')
    setDisplay(me.display_name); setEmail(me.email); setPhone(me.phone ?? '')
  }, [me])

  const changes: Record<string, boolean> = {
    first: canName && first !== (me.first_name ?? ''),
    last: canName && last !== (me.last_name ?? ''),
    display: canName && display !== me.display_name,
    email: canEmail && email.trim().toLowerCase() !== me.email,
    phone: canPhone && phone !== (me.phone ?? ''),
    avatar: canAvatar && avatarStaged !== null,
  }
  const dirty = Object.values(changes).some(Boolean)
  const count = Object.values(changes).filter(Boolean).length

  useSaveBar({
    dirty,
    count,
    invalid: display.trim().length < 2 ? 'Le nom affiché doit faire au moins 2 caractères.'
      : changes['email'] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'Adresse e-mail invalide.' : null,
    successMessage: 'Profil mis à jour',
    onSave: async () => {
      const body: Record<string, unknown> = {}
      if (changes['first']) body['first_name'] = first.trim() || null
      if (changes['last']) body['last_name'] = last.trim() || null
      if (changes['display']) body['display_name'] = display.trim()
      if (changes['email']) body['email'] = email.trim().toLowerCase()
      if (changes['phone']) body['phone'] = phone.trim() || null
      if (Object.keys(body).length > 0) {
        // Le changement d'e-mail déclenchera STEPUP_REQUIRED : adminMutate
        // ouvre la ré-authentification et REJOUE — aucune valeur perdue.
        const r = await adminMutate<{ error?: string; message?: string }>('PATCH', '/api/v1/me/profile', body)
        if (r.status >= 300) return r.data.message ?? 'Le profil n’a pas été modifié.'
      }
      if (changes['avatar'] && avatarStaged) {
        const r = avatarStaged === 'delete'
          ? await adminMutate<{ error?: string; message?: string }>('DELETE', '/api/v1/me/avatar')
          : await adminUpload<{ error?: string; message?: string }>('PUT', uploadPath('/api/v1/me/avatar', avatarStaged.crop ?? null), avatarStaged.file)
        if (r.status >= 300) return `Avatar : ${r.data.message ?? 'refusé.'}`
        setAvatarStaged(null)
      }
      props.onSaved()
      return true
    },
    onCancel: () => {
      setFirst(me.first_name ?? ''); setLast(me.last_name ?? '')
      setDisplay(me.display_name); setEmail(me.email); setPhone(me.phone ?? '')
      setAvatarStaged(null)
    },
  })

  const avatarUrl = avatarStaged && avatarStaged !== 'delete' ? avatarStaged.url
    : avatarStaged === 'delete' ? null : me.avatar

  const field = (
    id: string, label: string, value: string, set: (v: string) => void,
    allowed: boolean, opts: { type?: string | undefined; hint?: string | undefined } = {},
  ) => (
    <div className="lc-field">
      <label className="lc-label" htmlFor={id}>
        {label}
        {!allowed ? <span className="pf-locked"><Lock size={11} /> lecture seule</span> : null}
      </label>
      <input id={id} className="lc-input" type={opts.type ?? 'text'} value={value}
        disabled={!allowed} onChange={(e) => set(e.target.value)} />
      {opts.hint ? <p className="lc-form__help">{opts.hint}</p> : null}
    </div>
  )

  return (
    <section className="lc-panel gd-panel" aria-labelledby="pf-id">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="pf-id"><UserCircle size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Identité</h2>
      </header>
      <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
        <div className="pf-identity">
          <div className="pf-avatar-edit">
            <span className="lc-avatar pf-avatar">
              {avatarUrl ? <img className="lc-avatar__img" src={avatarUrl} alt="" /> : initialsOf(display || me.display_name)}
            </span>
            {canAvatar ? (
              <div className="pf-avatar-actions">
                <input type="file" hidden ref={fileRef} accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    e.target.value = ''
                    if (f) { setCropFile(f); setCropOpen(true) }
                  }} />
                <button className="lc-btn" onClick={() => fileRef.current?.click()}>
                  <Upload size={14} /> Changer
                </button>
                {avatarUrl ? (
                  <button className="lc-btn lc-btn--ghost" title="Revenir aux initiales (appliqué à l’enregistrement)"
                    onClick={() => setAvatarStaged(me.avatar ? 'delete' : null)}>
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ) : <span className="pf-locked"><Lock size={11} /> avatar en lecture seule</span>}
          </div>
          <div className="rm-fields" style={{ flex: 1 }}>
            {field('pf-first', 'Prénom', first, setFirst, canName)}
            {field('pf-last', 'Nom', last, setLast, canName)}
            {field('pf-display', 'Nom affiché', display, setDisplay, canName, { hint: 'C’est ce nom que voient les autres utilisateurs.' })}
            {field('pf-email', 'Adresse e-mail', email, setEmail, canEmail, {
              type: 'email',
              hint: canEmail
                ? 'Changement sensible : une confirmation forte vous sera demandée à l’enregistrement. Aucune vérification par lien n’existe encore (capacité future).'
                : undefined,
            })}
            {field('pf-phone', 'Téléphone', phone, setPhone, canPhone, { type: 'tel', hint: 'Optionnel — informatif, aucun SMS n’est envoyé.' })}
          </div>
        </div>
      </div>

      {cropFile ? (
        <CropperModal
          open={cropOpen}
          file={cropFile}
          title="Cadrer l’avatar"
          round
          onClose={() => setCropOpen(false)}
          onExited={() => setCropFile(null)}
          onDone={(crop) => {
            setAvatarStaged({ file: cropFile, url: URL.createObjectURL(cropFile), crop })
            setCropOpen(false)
          }}
        />
      ) : null}
    </section>
  )
}

/* ═══════════════ PASSKEYS ═══════════════ */

function PasskeySection(props: {
  me: { passkeys: number; mfa_enabled: boolean; preferred: string | null }
  onChanged: () => void
}) {
  const toast = useToast()
  const [keys, setKeys] = useState<Passkey[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [addMounted, setAddMounted] = useState(false)
  const [toDelete, setToDelete] = useState<Passkey | null>(null)

  const charger = () => {
    apiGet<{ credentials: Passkey[] }>('/api/v1/auth/webauthn/credentials')
      .then((j) => setKeys(j.credentials)).catch(() => setKeys([]))
  }
  useEffect(charger, [])

  const secureContext = window.isSecureContext
  const supported = typeof window.PublicKeyCredential !== 'undefined'
  const hasKeys = (keys?.length ?? 0) > 0

  const setPreferred = async (method: 'totp' | 'passkey') => {
    const r = await apiMutate<{ message?: string }>('PUT', '/api/v1/me/mfa-method', { method })
    if (r.status === 200) { toast('ok', `Méthode préférée : ${method === 'passkey' ? 'clé d’accès' : 'application d’authentification'}.`); props.onChanged() }
    else toast('error', r.data.message ?? 'Changement refusé.')
  }

  const remove = async (k: Passkey) => {
    // Suppression = action atomique sensible : step-up d'abord si besoin.
    const r = await adminMutate<{ message?: string; error?: string }>('DELETE', `/api/v1/auth/webauthn/credentials/${k.id}`)
    if (r.status === 200) { toast('ok', 'Clé d’accès supprimée.'); charger(); props.onChanged() }
    else toast('error', r.data.message ?? 'Suppression refusée.')
  }

  return (
    <div>
      <div className="pf-passkeys-head">
        <span className="lc-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Fingerprint size={14} /> Clés d’accès (passkeys)
        </span>
        <span className="lc-panel__spacer" />
        {supported ? (
          <button className="lc-btn" onClick={() => { setAdding(true); setAddMounted(true) }}>
            <Plus size={14} /> Ajouter une clé d’accès
          </button>
        ) : null}
      </div>
      <p className="lc-form__help" style={{ margin: '4px 0 10px' }}>
        Une clé d’accès (Touch ID, Windows Hello, YubiKey, iPhone…) remplace le
        code à 6 chiffres comme deuxième facteur. Le mot de passe reste requis —
        la connexion sans mot de passe n’est pas activée.
      </p>
      {!supported ? (
        // Diagnostic COMPRÉHENSIBLE (§40) : la cause la plus fréquente est
        // l'accès sans HTTPS — on donne la solution, pas une erreur technique.
        <div className="adm-note" style={{ marginTop: 0 }}>
          {!secureContext ? (
            <>
              <strong>Les clés d’accès nécessitent une connexion HTTPS sécurisée.</strong><br />
              Ouvrez Gestion via <span className="ad-mono">https://gestion.liveclubrennes.com</span> pour
              les activer — tout le reste fonctionne normalement ici.
            </>
          ) : (
            <>Ce navigateur ne prend pas en charge les clés d’accès — utilisez
            l’application d’authentification (TOTP).</>
          )}
        </div>
      ) : null}

      {keys === null ? <p className="lc-card__desc">Chargement…</p> : null}
      {keys?.length === 0 ? <p className="lc-card__desc">Aucune clé d’accès enregistrée.</p> : null}
      {keys?.map((k) => (
        <div key={k.id} className="pf-passkey">
          <Fingerprint size={16} style={{ color: 'var(--text-2)', flex: 'none' }} />
          <span className="pf-passkey__meta">
            <span className="pf-passkey__label">{k.label ?? 'Clé d’accès'}</span>
            <span className="pf-passkey__hint">
              Ajoutée {fmt(k.created_at)}{k.last_used_at ? ` · utilisée ${fmt(k.last_used_at)}` : ' · jamais utilisée'}
              {k.backed_up ? ' · synchronisée multi-appareils' : ''}
            </span>
          </span>
          <button className="lc-btn lc-btn--ghost lc-btn--icon" title="Supprimer cette clé" aria-label={`Supprimer ${k.label ?? 'la clé'}`}
            onClick={() => setToDelete(k)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      {(hasKeys && props.me.mfa_enabled) ? (
        <div className="pf-preferred">
          <span className="lc-label">Méthode préférée à la connexion</span>
          <div className="lc-tabs ap-seg" role="group" aria-label="Méthode MFA préférée">
            <button className={`lc-tabs__tab${(props.me.preferred ?? 'passkey') === 'passkey' ? ' lc-tabs__tab--active' : ''}`}
              onClick={() => void setPreferred('passkey')}>Clé d’accès</button>
            <button className={`lc-tabs__tab${props.me.preferred === 'totp' ? ' lc-tabs__tab--active' : ''}`}
              onClick={() => void setPreferred('totp')}>Application (TOTP)</button>
          </div>
          <p className="lc-form__help">Les autres méthodes restent disponibles en secours — rien n’est supprimé.</p>
        </div>
      ) : null}

      {addMounted ? (
        <AddPasskeyModal open={adding} onClose={() => setAdding(false)}
          onExited={() => setAddMounted(false)}
          onAdded={() => { setAdding(false); charger(); props.onChanged() }} />
      ) : null}

      <ConfirmDialog
        open={toDelete !== null}
        danger
        title={`Supprimer « ${toDelete?.label ?? 'cette clé'} » ?`}
        confirmLabel="Supprimer la clé"
        onConfirm={() => { const k = toDelete; setToDelete(null); if (k) void remove(k) }}
        onClose={() => setToDelete(null)}
      >
        Cette clé d’accès ne pourra plus servir à vous connecter. Si c’est
        votre dernière méthode de double authentification, la suppression sera
        refusée. Une confirmation forte vous sera demandée.
      </ConfirmDialog>
    </div>
  )
}

function AddPasskeyModal(props: { open: boolean; onClose: () => void; onExited: () => void; onAdded: () => void }) {
  const toast = useToast()
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const run = async () => {
    if (busy) return
    setBusy(true)
    setErr(null)
    try {
      // 1. Step-up (le serveur l'exige — adminMutate ouvre le dialogue).
      const opts = await adminMutate<{ options?: Record<string, unknown>; message?: string; error?: string }>(
        'POST', '/api/v1/auth/webauthn/register/options',
      )
      if (opts.status !== 200 || !opts.data.options) {
        setErr(opts.data.message ?? 'Impossible de démarrer l’ajout — réessayez.')
        return
      }
      // 2. Cérémonie WebAuthn dans le navigateur (biométrie / clé physique).
      const attestation = await startRegistration({ optionsJSON: opts.data.options as never })
      // 3. Vérification serveur + enregistrement.
      const v = await adminMutate<{ ok?: boolean; message?: string; error?: string }>(
        'POST', '/api/v1/auth/webauthn/register/verify',
        { response: attestation, label: label.trim() || undefined },
      )
      if (v.status === 200) { toast('ok', 'Clé d’accès ajoutée.'); props.onAdded() }
      else setErr(v.data.message ?? 'La clé n’a pas pu être enregistrée.')
    } catch (e) {
      const name = (e as { name?: string }).name
      setErr(name === 'NotAllowedError'
        ? 'Cérémonie annulée ou expirée — aucun changement.'
        : 'La création de la clé a échoué dans le navigateur.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={props.open} onClose={props.onClose} onExited={props.onExited} ariaLabel="Ajouter une clé d'accès" width={480}>
      <header className="lc-modal__head">
        <span className="lc-modal__icon"><Fingerprint size={20} /></span>
        <div className="lc-modal__title">Ajouter une clé d’accès</div>
      </header>
      <div className="lc-modal__body">
        <p className="lc-card__desc" style={{ marginBottom: 'var(--sp-4)' }}>
          Donnez un nom à cette clé pour la reconnaître plus tard, puis suivez
          les instructions de votre appareil (biométrie, code ou clé physique).
          Une confirmation forte est demandée avant l’ajout.
        </p>
        {err ? <p className="lc-form__error" role="alert">{err}</p> : null}
        <div className="lc-field">
          <label className="lc-label" htmlFor="pk-label">Nom de la clé</label>
          <input id="pk-label" className="lc-input" placeholder="iPhone, MacBook, YubiKey…" maxLength={60}
            value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="lc-dialog__actions">
          <button className="lc-btn lc-btn--ghost" onClick={props.onClose} disabled={busy}>Annuler</button>
          <button className="lc-btn lc-btn--primary" onClick={() => void run()} disabled={busy}>
            {busy ? <span className="lc-spin" aria-hidden="true" /> : <Fingerprint size={15} />}
            {busy ? 'Cérémonie en cours…' : 'Créer la clé'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ─── formulaire mot de passe (action de sécurité atomique — pas une
   édition de valeurs : le verbe est « Modifier le mot de passe ») ─── */

function PasswordForm() {
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    if (next !== confirm) { toast('error', 'La confirmation ne correspond pas.'); return }
    setBusy(true)
    const r = await apiMutate<{ ok: boolean; message?: string }>('POST', '/api/v1/auth/password', { current, next })
    setBusy(false)
    if (r.status === 200) {
      toast('ok', 'Mot de passe modifié — vos autres appareils ont été déconnectés.')
      setCurrent(''); setNext(''); setConfirm('')
    } else {
      toast('error', r.data.message ?? 'Modification refusée.')
    }
  }

  return (
    <div className="lc-form" style={{ maxWidth: 420 }}>
      <div className="lc-field">
        <label className="lc-label" htmlFor="pf-cur">Mot de passe actuel</label>
        <input id="pf-cur" className="lc-input" type="password" autoComplete="current-password"
          value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="lc-field">
        <label className="lc-label" htmlFor="pf-new">Nouveau mot de passe</label>
        <input id="pf-new" className="lc-input" type="password" autoComplete="new-password"
          value={next} onChange={(e) => setNext(e.target.value)} />
      </div>
      <div className="lc-field">
        <label className="lc-label" htmlFor="pf-conf">Confirmer</label>
        <input id="pf-conf" className="lc-input" type="password" autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </div>
      <button className="lc-btn lc-btn--primary" disabled={busy || !current || next.length < 1} onClick={() => void submit()}>
        {busy ? 'Modification…' : 'Modifier le mot de passe'}
      </button>
    </div>
  )
}
